/**
 * AgentDownloadsPage — Public landing page for the VOS Studio Agent installer.
 *
 * Founder ask May 17, 2026: artists need to hit vawworld.in on the
 * workstation they want to install the agent on and download from
 * there. This page is intentionally PUBLIC (no auth) so anyone can
 * land here directly via the login-screen footer link.
 *
 * Until the Tauri Phase 2 build ships, the buttons are marked "Coming
 * soon" with the expected file path so the founder knows where to
 * drop the actual MSI/DMG/.deb once built on a Windows / Mac box.
 *
 * When the binary lands, the backend exposes it at:
 *   GET /api/agent/release/windows  → 302 to /static/agent/VOSAgent-1.0.0-x64.msi
 *   GET /api/agent/release/macos    → 302 to /static/agent/VOSAgent-1.0.0.dmg
 *   GET /api/agent/release/linux    → 302 to /static/agent/VOSAgent-1.0.0.deb
 *
 * The current page only needs to render the buttons; once the
 * backend route exists, clicks Just Work.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Monitor, Download, Apple, Cpu, Shield, Eye, Pause, FileText,
  ArrowLeft, CheckCircle2, Clock,
} from 'lucide-react';

const PLATFORMS = [
  {
    key: 'windows',
    name: 'Windows 10 / 11',
    icon: Monitor,
    file: 'VOSAgent-1.0.0-x64.msi',
    sizeHint: '~10 MB',
    tint: 'sky',
    note: 'Most artists. Right-click → Run as administrator. Defender will say "Windows protected your PC" — click "More info" → "Run anyway" (we are not code-signed yet).',
  },
  {
    key: 'macos',
    name: 'macOS (Intel + Apple Silicon)',
    icon: Apple,
    file: 'VOSAgent-1.0.0-universal.dmg',
    sizeHint: '~12 MB',
    tint: 'zinc',
    note: 'Freelancers on MacBooks. Drag VOS Agent.app to Applications. First launch: right-click → Open.',
  },
  {
    key: 'linux',
    name: 'Linux (Ubuntu / Debian)',
    icon: Cpu,
    file: 'vos-agent_1.0.0_amd64.deb',
    sizeHint: '~8 MB',
    tint: 'amber',
    note: 'Power users / render nodes. sudo dpkg -i vos-agent_1.0.0_amd64.deb',
  },
];

const TINT = {
  sky:   { ring: 'ring-sky-500/40',   text: 'text-sky-300',   bg: 'bg-sky-500/10',   btn: 'bg-sky-500 hover:bg-sky-400 text-zinc-950' },
  zinc:  { ring: 'ring-zinc-500/40',  text: 'text-zinc-300',  bg: 'bg-zinc-500/10',  btn: 'bg-zinc-300 hover:bg-zinc-200 text-zinc-950' },
  amber: { ring: 'ring-amber-500/40', text: 'text-amber-300', bg: 'bg-amber-500/10', btn: 'bg-amber-400 hover:bg-amber-300 text-zinc-950' },
};

export default function AgentDownloadsPage() {
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
  const [releases, setReleases] = useState({});  // platform → {available, file, size, version}

  // Probe the backend for which platforms have an actual binary ready.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${apiBase}/api/agent/releases`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setReleases(data.releases || {});
      } catch { /* leave empty — UI will show "Coming soon" */ }
    })();
    return () => { cancelled = true; };
  }, [apiBase]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="agent-downloads-page">
      {/* Header strip */}
      <div className="border-b border-zinc-900 bg-gradient-to-b from-zinc-900 to-zinc-950">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <Link to="/login" className="inline-flex items-center gap-2 text-[12px] text-zinc-500 hover:text-zinc-300 mb-6">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to login
          </Link>
          <div className="flex items-start gap-5">
            <div className="w-14 h-14 rounded-xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shrink-0">
              <Monitor className="w-7 h-7 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">VOS Studio Agent</h1>
              <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
                A lightweight tray app for your workstation. Tracks the time you spend on
                each shot, opens Blender files with one click, dispatches renders to your
                in-house RTX farm, and keeps studio IP safe. Installs in under a minute.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Download cards */}
      <div className="max-w-5xl mx-auto px-6 py-10">
        <h2 className="text-sm font-semibold text-zinc-300 mb-4 flex items-center gap-2">
          <Download className="w-4 h-4 text-emerald-400" /> Download for your platform
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PLATFORMS.map(p => {
            const tint = TINT[p.tint];
            const rel  = releases[p.key];
            const available = !!rel?.available;
            const fileName  = rel?.file || p.file;
            const size      = rel?.size_human || p.sizeHint;
            const version   = rel?.version || '1.0.0';
            return (
              <div key={p.key}
                   data-testid={`download-card-${p.key}`}
                   className={`relative rounded-xl border border-zinc-800 ${tint.bg} ring-1 ${tint.ring} p-5 flex flex-col gap-3`}>
                <div className="flex items-center gap-2">
                  <p.icon className={`w-5 h-5 ${tint.text}`} />
                  <h3 className="text-sm font-semibold">{p.name}</h3>
                </div>
                <div className="flex-1">
                  <p className="text-[11px] text-zinc-500 mb-1">{p.note}</p>
                  <p className="text-[10px] text-zinc-600 font-mono mt-2 truncate" title={fileName}>{fileName}</p>
                  <p className="text-[10px] text-zinc-600">v{version} · {size}</p>
                </div>
                {available ? (
                  <a
                    href={`${apiBase}/api/agent/release/${p.key}`}
                    className={`text-center text-[12px] font-semibold py-2 rounded-md ${tint.btn} transition-colors`}
                    data-testid={`download-btn-${p.key}`}
                  >
                    <Download className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
                    Download
                  </a>
                ) : (
                  <span
                    className="text-center text-[12px] font-semibold py-2 rounded-md bg-zinc-800/80 text-zinc-500 flex items-center justify-center gap-1.5 cursor-not-allowed"
                    data-testid={`download-coming-${p.key}`}
                    title="Build pending — Phase 2 of the agent rollout"
                  >
                    <Clock className="w-3.5 h-3.5" />
                    Coming soon
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* What the agent does */}
        <section className="mt-14">
          <h2 className="text-sm font-semibold text-zinc-300 mb-4">What VOS Studio Agent does</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[13px]">
            {[
              { icon: Clock,        title: 'Accurate time tracking',         body: 'Detects idle (>5 min) and active windows. Auto-matches the .blend you have open to a VOS task — your hours are measured, not guessed.' },
              { icon: Monitor,      title: 'One-click "Open in Blender"',    body: 'Click any shot card in VOS → Blender launches with that file pre-loaded. No more navigating the NAS.' },
              { icon: Cpu,          title: 'Render farm dispatch',           body: 'Sends jobs from VOS straight to your in-house RTX 5090 farm via Deadline. Watches worker health and auto-restarts dropped Deadline Workers.' },
              { icon: Shield,       title: 'Studio IP protection',           body: 'Flags USB plug-ins, screenshots, and copy attempts from VOS folders. Blocks entertainment sites during work hours.' },
              { icon: Eye,          title: 'Live workforce dashboard',       body: 'Founder dashboard lights up the moment you sign in — what you are working on, how long, on which file.' },
              { icon: Pause,        title: 'Pause anytime',                  body: 'Tray icon → 60-min pause. Doctor appointment, lunch, personal task — your call. Activity gap logged, no detail captured.' },
            ].map((f, i) => (
              <div key={i} className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-4 flex gap-3">
                <f.icon className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-zinc-200 font-semibold">{f.title}</p>
                  <p className="text-[12px] text-zinc-400 mt-0.5">{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Privacy contract */}
        <section className="mt-14">
          <h2 className="text-sm font-semibold text-zinc-300 mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-400" /> Privacy contract
          </h2>
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-[13px] text-zinc-300 space-y-2">
            {[
              'We measure effort, not content. We log which app you have open, never what is on screen.',
              'Only the VOS_Work folder is watched. Personal files, photos, browser history are never scanned.',
              'We do not keylog. We detect that a key was pressed (for idle detection), never which key.',
              'USB plug-ins and copy attempts from VOS folders are logged for IP protection. Outside VOS work, your usage is your business.',
              'Pause monitoring for 60 minutes anytime via the tray icon. Logged as a gap, no detail captured.',
              'Export your own KPI history as CSV anytime.',
              'Entertainment sites blocked during work hours. YouTube allowlist permits tutorial channels (Blender Guru, CG Cookie, etc.).',
            ].map((p, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                <p>{p}</p>
              </div>
            ))}
            <p className="text-[12px] text-zinc-500 mt-3">
              By installing the agent you acknowledge the above. Questions: <a href="mailto:kumar@vawworld.tv" className="text-emerald-300 hover:underline">kumar@vawworld.tv</a>
            </p>
          </div>
        </section>

        {/* Footer */}
        <p className="text-center text-[11px] text-zinc-600 mt-14">
          © VawworldOS · <Link to="/login" className="hover:text-zinc-400">Back to login</Link>
        </p>
      </div>
    </div>
  );
}
